import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const typeIcon: Record<string, string> = {
  success: "ri-checkbox-circle-line",
  info: "ri-information-line",
  warning: "ri-alert-line",
  error: "ri-error-warning-line",
};

const typeColor: Record<string, string> = {
  success: "text-cyber-green",
  info: "text-electric-blue",
  warning: "text-cyber-orange",
  error: "text-cyber-red",
};

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all cursor-pointer"
        aria-label="Notifications"
      >
        <i className="ri-notification-3-line text-base" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-cyber-red text-white text-xs font-bold leading-none">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-dark-panel border border-dark-border rounded-2xl overflow-hidden z-50 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
            <span className="text-white font-semibold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-silver-dim text-sm">No notifications</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-dark-border/50 cursor-pointer hover:bg-dark-card/50 transition-colors ${!n.read ? "bg-electric-blue/3" : ""}`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full bg-dark-card flex-shrink-0 mt-0.5 ${typeColor[n.type]}`}>
                    <i className={`${typeIcon[n.type]} text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-semibold ${!n.read ? "text-white" : "text-silver"}`}>{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-electric-blue flex-shrink-0" />}
                    </div>
                    <p className="text-silver-dim text-xs mt-0.5 leading-relaxed">{n.message}</p>
                    <span className="text-silver-dim text-xs mt-1 block">{timeAgo(n.createdAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-dark-border">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="text-electric-blue text-xs font-medium hover:underline cursor-pointer"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
